import { useEffect, useRef, useState } from 'react'

/** ~30fps. Every reveal re-runs `marked.lexer` on the growing text, so a full
 *  60fps buys no perceptible smoothness for double the parse cost. */
const REVEAL_INTERVAL_MS = 32
/** A single reveal never dumps more than this, so a big paste still reads as
 *  writing rather than as a jump. */
const MAX_CHARS_PER_REVEAL = 120
/** While the stream is live the backlog drains proportionally: small chunks
 *  trickle out a character at a time, a burst catches up fast, and the reveal
 *  can never fall unboundedly behind. */
const STREAMING_DIVISOR = 10
const STREAMING_MIN_CHARS = 1
/** Once the turn is over there's nothing left to pace against — drain hard so
 *  the final text lands immediately. */
const DRAINING_DIVISOR = 3
const DRAINING_MIN_CHARS = 4

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/**
 * Smooths a streamed string into a paced reveal. The caller keeps passing the
 * full accumulated `content` — this only decides how much of it is on screen,
 * so the message itself stays the single source of truth and the transport
 * layer never has to know about animation.
 */
export function useSmoothStreamText(content: string, isStreaming: boolean): string {
  const [revealedLength, setRevealedLength] = useState(() =>
    isStreaming && !prefersReducedMotion() ? 0 : content.length,
  )
  const revealedRef = useRef(revealedLength)
  const previousContentRef = useRef(content)
  // Held across effect runs: a new chunk arriving must not reset the throttle,
  // otherwise a fast stream reveals on every frame instead of every other one.
  const lastRevealAtRef = useRef(0)

  useEffect(() => {
    if (prefersReducedMotion()) {
      revealedRef.current = content.length
      setRevealedLength(content.length)
      return
    }

    // Replaced rather than appended to (a reset, or a row reused for a
    // different turn) — start the reveal over.
    if (!content.startsWith(previousContentRef.current)) {
      revealedRef.current = 0
      setRevealedLength(0)
    }
    previousContentRef.current = content

    if (revealedRef.current >= content.length) return

    let frame = 0

    const step = (now: number) => {
      const elapsed = now - lastRevealAtRef.current
      if (elapsed >= REVEAL_INTERVAL_MS) {
        lastRevealAtRef.current = now
        const backlog = content.length - revealedRef.current
        const divisor = isStreaming ? STREAMING_DIVISOR : DRAINING_DIVISOR
        const minChars = isStreaming ? STREAMING_MIN_CHARS : DRAINING_MIN_CHARS
        // A throttled/backgrounded tab can skip many intervals between two
        // successful ticks. Scale the cap by how many intervals actually
        // elapsed so the reveal catches fully up instead of being capped as
        // if only one ~32ms tick had passed.
        const intervalsElapsed = Math.max(1, Math.floor(elapsed / REVEAL_INTERVAL_MS))
        const cap = MAX_CHARS_PER_REVEAL * intervalsElapsed
        const chars = Math.min(Math.max(Math.round(backlog / divisor), minChars), cap)
        revealedRef.current = Math.min(revealedRef.current + chars, content.length)
        setRevealedLength(revealedRef.current)
        if (revealedRef.current >= content.length) return
      }
      frame = requestAnimationFrame(step)
    }

    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [content, isStreaming])

  return revealedLength >= content.length ? content : content.slice(0, revealedLength)
}
